import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiChevronDown, FiChevronUp, FiMail } from 'react-icons/fi';
import './SupportPage.css';

const faqs = [
  {
    category: 'Getting Started',
    items: [
      {
        q: 'How do I create a golf society?',
        a: 'After signing in, tap Manage from the menu. Select "Create a Society", give it a name and location, and you\'re set. You\'ll become the society admin and can invite other members.',
      },
      {
        q: 'How do I join an existing society?',
        a: 'Ask your society admin for the invite code. On the Setup screen, tap "Join a Society" and enter the code. Once accepted by an admin, you\'ll have access to all society data.',
      },
      {
        q: 'Can I be a member of more than one society?',
        a: 'Yes. You can join multiple societies under the same account. Use the society switcher on the home screen to move between them.',
      },
    ],
  },
  {
    category: 'Scoring & Handicaps',
    items: [
      {
        q: 'How is my playing handicap calculated?',
        a: 'It depends on how the tournament is set up. If course-adjusted handicaps are enabled, your playing handicap is calculated using the WHS formula: round(handicap_index × slope ÷ 113 + (course_rating − par)). If the tournament does not use course-adjusted handicaps, your playing handicap is simply your handicap index rounded to the nearest whole number. Either way, it is applied automatically when a round is set up.',
      },
      {
        q: 'How is my Stableford score calculated?',
        a: 'Points are awarded per hole relative to net par: eagle or better = 4 pts, birdie = 3, par = 2, bogey = 1, double bogey or worse = 0.',
      },
      {
        q: 'When does my handicap index update?',
        a: 'Handicap adjustments only apply if your society has its own handicap adjustment system configured — admins can set this up and manage it from the Manage section. If adjustments are enabled, your handicap index is updated automatically once an admin finalises a round, based on your Stableford score for that round. If no adjustment system is configured, your handicap index stays the same until an admin updates it manually.',
      },
      {
        q: 'My scores aren\'t appearing on the leaderboard — what should I do?',
        a: 'First check you have an internet connection and pull down to refresh. If you entered scores while offline, open your scorecard and tap Sync. If the problem continues, contact us with your society name and round details.',
      },
    ],
  },
  {
    category: 'Rounds & Tournaments',
    items: [
      {
        q: 'How do I enter scores for a round?',
        a: 'From the home screen or bottom nav, tap Play Golf then select your tournament and round. You\'ll see your group\'s scorecard. Tap each hole to enter the gross score — Stableford points calculate automatically.',
      },
      {
        q: 'Can I enter scores offline?',
        a: 'Yes. 1-19 Golf has offline scoring support. Scores are saved locally and synced to the server the next time you have a connection. Look out for the offline indicator at the top of the screen.',
      },
      {
        q: 'What happens after a round is finalised?',
        a: 'Once an admin finalises a round, scores are locked, handicap adjustments are applied, and the overall standings are updated. You\'ll see the finalised round marked on the leaderboard.',
      },
      {
        q: 'What formats does the app support?',
        a: '1-19 Golf supports individual Stableford, Stroke Play, and Matchplay formats across single and multi-round tournaments.',
      },
    ],
  },
  {
    category: 'Players & Courses',
    items: [
      {
        q: 'How do I add players to a society?',
        a: 'Society admins can add players from the Manage section. Go to Manage → Players and tap "Add Player". You can enter the player\'s name, handicap index, and other details. Once added, players can be linked to member accounts and entered into tournaments.',
      },
      {
        q: 'Can players edit their own details?',
        a: 'Players can update their display name via their profile page. Handicap index and other player details (such as name and linked account) are managed by society admins to keep records consistent across the society.',
      },
      {
        q: 'What is the Global Course Directory?',
        a: '1-19 Golf maintains a growing Global Course Directory containing course and hole data for courses around the world. Admins can search this directory when setting up a tournament to quickly add a course without entering all the details manually. The directory is continuously expanded over time.',
      },
      {
        q: 'Can my society add or edit its own courses?',
        a: 'Yes. Each society maintains its own list of courses. Society admins can add new courses, edit existing course details (including hole pars, stroke indexes, and tee information), and remove courses from the society\'s list. Changes made to a society\'s courses do not affect the Global Directory or other societies.',
      },
    ],
  },
  {
    category: 'Account & Data',
    items: [
      {
        q: 'How do I reset my password?',
        a: 'On the login screen, tap "Forgot password" and enter your email address. A reset link will arrive within a few minutes. Check your spam folder if it doesn\'t appear.',
      },
      {
        q: 'How do I update my display name or profile?',
        a: 'Tap My Profile from the menu. From there you can update your display name and view your linked player profile.',
      },
      {
        q: 'How do I delete my account?',
        a: 'Go to My Profile (tap your avatar) and scroll to the bottom. Tap "Delete Account" and follow the confirmation steps. Your login and society memberships will be permanently removed. Your scoring history is anonymised and retained so the society\'s records remain complete.',
      },
      {
        q: 'What data does 1-19 Golf store about me?',
        a: 'We store your name, email address, golf scores, handicap history, and society membership details. We do not sell your data to third parties. For full details, see our Privacy Policy.',
      },
    ],
  },
];

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? 'open' : ''}`}>
      <button className="faq-question" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span>{q}</span>
        {open ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
      </button>
      {open && <div className="faq-answer"><p>{a}</p></div>}
    </div>
  );
};

const ContactCollapsible = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="support-contact-collapsible">
      <button className="support-contact-toggle" onClick={() => setOpen(o => !o)}>
        <span>Can't find what you are looking for?</span>
        {open ? <FiChevronUp size={15} /> : <FiChevronDown size={15} />}
      </button>
      {open && (
        <div className="support-contact-body">
          <p>You can reach us by email and we'll get back to you as soon as we can.</p>
          <a href="mailto:1to19golf@gmail.com">
            <FiMail size={13} />
            1to19golf@gmail.com
          </a>
        </div>
      )}
    </div>
  );
};

const SupportPage = () => {
  const navigate = useNavigate();

  return (
    <div className="legal-page support-page">
      <div className="legal-container">
        <button className="legal-back-btn" onClick={() => navigate(-1)}>
          <FiArrowLeft size={18} />
          <span>Back</span>
        </button>

        <h1>Support</h1>
        <p className="legal-updated">Find answers to common questions below.</p>

        <section>
          {faqs.map(group => (
            <div key={group.category} className="faq-group">
              <h3>{group.category}</h3>
              {group.items.map(item => (
                <FaqItem key={item.q} q={item.q} a={item.a} />
              ))}
            </div>
          ))}
        </section>

        <section>
          <h2>Legal</h2>
          <p>
            <a href="/legal?tab=privacy">Privacy Policy</a>
            {' · '}
            <a href="/legal?tab=terms">Terms &amp; Conditions</a>
          </p>
        </section>

        <div className="support-contact-footer">
          <ContactCollapsible />
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
